import { logGlobalError } from '../utils/globalErrorLogger';

let installed = false;

interface BrowserStateEvent {
  event: string;
  atMs: number;
  online: boolean;
  visibility: DocumentVisibilityState;
}

const recentBrowserEvents: BrowserStateEvent[] = [];

function recordBrowserState(event: string): void {
  recentBrowserEvents.push({
    event,
    atMs: Math.round(performance.now()),
    online: navigator.onLine,
    visibility: document.visibilityState,
  });
  if (recentBrowserEvents.length > 10) recentBrowserEvents.shift();
}

function reportUnhandledAbort(error: Error): void {
  // A clients bundle frame is a source hint, not proof of a Firestore timeout.
  // Keep the original frames for source-map investigation across SDK versions.
  const sourceHint = /\/clients\.[^/\s:]+\.chunk\.js:\d+:\d+/.test(error.stack || '')
    ? 'firebase-clients-bundle'
    : 'unknown';
  const diagnostic = new Error(`Unhandled request cancellation: ${error.message}`);
  diagnostic.name = 'UnhandledAbortError';
  if (error.stack) {
    diagnostic.stack = `${diagnostic.name}: ${diagnostic.message}\n${error.stack.split('\n').slice(1).join('\n')}`;
  }
  logGlobalError(diagnostic, {
    context: 'unhandledrejection.abort',
    severity: 'ERROR',
    metadata: {
      diagnosticKind: 'unhandled-abort',
      sourceHint,
      originalName: error.name,
      originalMessage: error.message,
      originalStack: error.stack || null,
      online: navigator.onLine,
      visibility: document.visibilityState,
      documentHasFocus: document.hasFocus(),
      pageAgeMs: Math.round(performance.now()),
      recentBrowserEvents: recentBrowserEvents.map((event) => ({ ...event })),
    },
  });
}

function isMonacoCancellation(reason: unknown): boolean {
  if (!(reason instanceof Error)) return false;
  if (reason.message !== 'Canceled') return false;
  return typeof reason.stack === 'string' && reason.stack.includes('monaco-editor');
}

function isMathJaxError(reason: unknown): boolean {
  if (!(reason instanceof Error)) return false;
  return reason.message.includes('Typesetting failed')
    || reason.message.includes('Failed to download MathJax');
}

/**
 * Chrome fires these at `window.onerror` whenever a ResizeObserver callback
 * causes a resize that has to be delivered in a later pass. The layout still
 * settles — nothing is broken, and the event carries no stack, so it is
 * unactionable in Error Reporting. Fix the re-entrancy at the observer
 * callsite (defer the write out of the callback); do not read these as bugs.
 */
// Some browsers dispatch this to `window.onerror` with a `Uncaught ` or
// `Error: ` prefix (or both) on `event.message`, so tolerate them here — the
// filter must catch every form we see in production, otherwise the noise
// leaks into Error Reporting.
const RESIZE_OBSERVER_NOISE =
  /^(?:Uncaught\s+)?(?:[A-Za-z]*Error:\s+)?ResizeObserver loop (?:limit exceeded|completed with undelivered notifications)/;

function isResizeObserverNoise(message: string): boolean {
  return RESIZE_OBSERVER_NOISE.test(message);
}

const BROWSER_EXTENSION_URL = /\b(?:chrome|moz|edge|safari-web)-extension:\/\//i;

/**
 * True when every stack frame lives inside a browser extension bundle
 * (e.g. MetaMask, a password manager, a wallet extension). These errors
 * originate outside our code and cannot be reproduced or fixed here, so
 * they should not enter Error Reporting.
 *
 * If any frame is not an extension frame, the extension called into our
 * code and the failure could be real — let it through.
 */
function isBrowserExtensionError(reason: unknown, location?: ReportedLocation): boolean {
  if (location?.source && BROWSER_EXTENSION_URL.test(location.source)) {
    const stack = stringProperty(reason, 'stack');
    if (!stack) return true;
    return isExtensionOnlyStack(stack);
  }
  const stack = stringProperty(reason, 'stack');
  if (!stack || !BROWSER_EXTENSION_URL.test(stack)) return false;
  return isExtensionOnlyStack(stack);
}

function isExtensionOnlyStack(stack: string): boolean {
  const frames = stack
    .split('\n')
    .slice(1)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  if (frames.length === 0) return false;
  return frames.every((frame) => BROWSER_EXTENSION_URL.test(frame));
}

function stringProperty(value: unknown, property: 'message' | 'name' | 'stack'): string | undefined {
  if (!value || typeof value !== 'object') return undefined;
  try {
    const propertyValue = (value as Record<string, unknown>)[property];
    return typeof propertyValue === 'string' ? propertyValue : undefined;
  } catch {
    return undefined;
  }
}

function fallbackMessage(value: unknown): string {
  if (typeof value === 'string') return value;
  try {
    return String(value);
  } catch {
    return 'Unknown error';
  }
}

interface ReportedLocation {
  source?: string;
  line?: number;
  column?: number;
}

/**
 * `instanceof Error` is false for errors created by another browser realm
 * (for example, an iframe or injected extension). Preserve an error-like
 * object's original stack instead of replacing it with a stack pointing at
 * this reporter, which would hide the actual failing callsite.
 */
function normalizeReportedError(
  value: unknown,
  message: string,
  location?: ReportedLocation,
): Error {
  if (value instanceof Error) return value;

  const error = new Error(stringProperty(value, 'message') || message);
  const name = stringProperty(value, 'name');
  const stack = stringProperty(value, 'stack');
  if (name) error.name = name;
  if (stack) {
    error.stack = stack;
  } else if (location?.source) {
    const suffix = location.line
      ? `:${location.line}${location.column ? `:${location.column}` : ''}`
      : '';
    error.stack = `${error.name}: ${error.message}\n    at ${location.source}${suffix}`;
  }
  return error;
}

/**
 * Install one-shot global listeners that forward uncaught errors and
 * unhandled promise rejections to Cloud Logging via `logGlobalError`,
 * which routes them through the ReportedErrorEvent path so they land in
 * Cloud Error Reporting (grouped) — not just Logs Explorer.
 *
 * Caveats:
 * - The underlying callable requires auth. Errors thrown before sign-in
 *   are silently dropped rather than buffered.
 * - React rendering errors caught by error boundaries do NOT bubble up to
 *   window.onerror. The app-level ErrorBoundary covers that path.
 */
export function installErrorReporter(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  recordBrowserState('reporter-installed');
  for (const type of ['online', 'offline', 'pageshow', 'pagehide']) {
    window.addEventListener(type, () => recordBrowserState(type));
  }
  document.addEventListener('visibilitychange', () => recordBrowserState('visibilitychange'));

  window.addEventListener('error', (event) => {
    if (event.message === 'Script error.' || event.message === 'Script error') return;
    if (
      isResizeObserverNoise(event.message)
      || isResizeObserverNoise(stringProperty(event.error, 'message') ?? '')
    ) return;
    const location = {
      source: event.filename,
      line: event.lineno,
      column: event.colno,
    };
    if (isBrowserExtensionError(event.error, location)) return;
    const error = normalizeReportedError(event.error, event.message, location);
    logGlobalError(error, {
      context: 'window.onerror',
      metadata: location,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    if (isMonacoCancellation(reason)) return;
    if (isMathJaxError(reason)) return;
    if (isBrowserExtensionError(reason)) return;
    // Nullish rejection reasons (Promise.reject() / Promise.reject(undefined)
    // from inside a third-party SDK dispatcher) carry no stack, code, or
    // callsite info and are unactionable in Error Reporting. Log at NOTICE so
    // the pattern is still queryable in Logs Explorer, but keep it out of the
    // grouped ERROR dashboard.
    if (reason === null || reason === undefined) {
      logGlobalError(new Error(`Unhandled promise rejection with ${reason === null ? 'null' : 'undefined'} reason`), {
        context: 'unhandledrejection.nullish',
        severity: 'NOTICE',
      });
      return;
    }
    const error = normalizeReportedError(reason, fallbackMessage(reason));
    if (error.name === 'AbortError') {
      reportUnhandledAbort(error);
      return;
    }
    logGlobalError(error, { context: 'unhandledrejection' });
  });
}

export const _internal = {
  fallbackMessage,
  isBrowserExtensionError,
  isResizeObserverNoise,
  normalizeReportedError,
  stringProperty,
};
